'use client';

import { Box, ToggleButton, ToggleButtonGroup, useMediaQuery, useTheme } from '@mui/material';
import { type ReactNode, useState } from 'react';
import { useI18n } from '../../hooks/useI18n';
import { GlassAlert } from '../GlassAlert/GlassAlert';
import { ShimmerLoader } from '../ShimmerLoader/ShimmerLoader';

export interface DocumentViewLayoutProps {
  /** Document renderer content (left panel on desktop) */
  renderer: ReactNode;
  /** Attributes panel content (right panel on desktop) */
  panel: ReactNode;
  /** Full-page loading state */
  loading?: boolean;
  /** Error message to display */
  error?: string;
}

export function DocumentViewLayout({ renderer, panel, loading, error }: DocumentViewLayoutProps) {
  const { t } = useI18n();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const [activeTab, setActiveTab] = useState<'document' | 'details'>('document');

  if (loading) {
    return (
      <Box sx={{ display: 'flex', gap: 2, height: '100%', minHeight: 400, p: 2 }}>
        <Box sx={{ flex: 3 }}>
          <ShimmerLoader variant="rectangular" width="100%" height="100%" />
        </Box>
        <Box sx={{ flex: 1 }}>
          <ShimmerLoader variant="rectangular" width="100%" height="100%" />
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <GlassAlert severity="error">{error}</GlassAlert>
      </Box>
    );
  }

  // Mobile: Tab toggle between document and details
  if (isMobile) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
        <Box sx={{ px: 2, pt: 1, pb: 1 }}>
          <ToggleButtonGroup
            value={activeTab}
            exclusive
            onChange={(_, value) => {
              if (value) setActiveTab(value);
            }}
            size="small"
            fullWidth
            sx={{ mb: 1 }}
          >
            <ToggleButton value="document">{t('documents.view.tabDocument')}</ToggleButton>
            <ToggleButton value="details">{t('documents.view.tabDetails')}</ToggleButton>
          </ToggleButtonGroup>
        </Box>
        <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>{activeTab === 'document' ? renderer : panel}</Box>
      </Box>
    );
  }

  // Tablet: Stacked layout (document above, details below)
  if (isTablet) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, overflow: 'auto' }}>
        <Box sx={{ flex: 2, minHeight: 300 }}>{renderer}</Box>
        <Box sx={{ borderTop: 1, borderColor: 'divider' }}>{panel}</Box>
      </Box>
    );
  }

  // Desktop: 75/25 split
  return (
    <Box sx={{ display: 'flex', height: '100%', minHeight: 0 }}>
      <Box
        sx={{
          flex: 3,
          minWidth: 0,
          overflow: 'hidden',
          bgcolor: 'action.hover',
        }}
      >
        {renderer}
      </Box>
      <Box
        sx={{
          flex: 1,
          minWidth: 280,
          maxWidth: 420,
          borderLeft: 1,
          borderColor: 'divider',
          overflow: 'auto',
        }}
      >
        {panel}
      </Box>
    </Box>
  );
}
